import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import AttivaAccount from './pages/AttivaAccount';
import AreaClienti from './pages/AreaClienti';
import NewsClienti from './pages/NewsClienti';
import CatalogoClienti from './pages/CatalogoClienti';
import CafPratiche from './pages/CafPratiche';
import PatronatoInps from './pages/PatronatoInps';
import CalendarioAppuntamenti from './pages/CalendarioAppuntamenti';
import MessaggiClienti from './pages/MessaggiClienti';
import AnnunciApp from './pages/AnnunciApp';
import RegistrazioniApp from './pages/RegistrazioniApp';
import Consensi from './pages/Consensi';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import Consulting from './pages/Consulting';
import Dimissioni from './pages/Dimissioni';
import PermessiSoggiorno from './pages/PermessiSoggiorno';
import Imu from './pages/Imu';
import EstrattoInps from './pages/EstrattoInps';
import AssegnoMaternita from './pages/AssegnoMaternita';
import Quotes from './pages/Quotes';
import Policies from './pages/Policies';
import Claims from './pages/Claims';
import Renewals from './pages/Renewals';
import Payments from './pages/Payments';
import TelecomContracts from './pages/TelecomContracts';
import TelecomSIMs from './pages/TelecomSIMs';
import TelecomBilling from './pages/TelecomBilling';
import UserManagement from './pages/UserManagement';
import BackupRestore from './pages/BackupRestore';
import AuditLog from './pages/AuditLog';
import SearchResults from './pages/SearchResults';
import AIAssistant from './pages/AIAssistant';
import CanoneRai from './pages/CanoneRai';
import CompanyProfile from './pages/CompanyProfile';
import Mutui from './pages/Mutui';
import Ospitalita from './pages/Ospitalita';
import NotFound from './pages/NotFound';
import Utilities from './pages/Utilities';
import SubAgenti from './pages/SubAgenti';
import Agenti from './pages/Agenti';
import Delega from './pages/Delega';
import ScannerDocumenti from './pages/ScannerDocumenti';
import TelecomScadenze from './pages/TelecomScadenze';
import NuovaDaBolletta from './pages/NuovaDaBolletta';
import FattureEstrazione from './pages/FattureEstrazione';
import SimulazioneOfferta from './pages/SimulazioneOfferta';
import PraticheLuceGas from './pages/PraticheLuceGas';
import ScadenzeLuceGas from './pages/ScadenzeLuceGas';
import TrascinaFile from './components/TrascinaFile';
import ErrorBoundary from './components/ErrorBoundary';

const ProtectedRoute = ({ children, requiredModule, adminOnly, superadminOnly, masterOnly }) => {
  const { user, loading, hasModuleAccess, isAdmin, isSuperAdmin, isMaster } = useAuth();

  // Show loading spinner while authentication state is being determined
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  // Only redirect to login if we're certain user is not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // SuperAdmin-only route check
  if (superadminOnly && !isSuperAdmin()) {
    return <Navigate to="/dashboard" replace />;
  }

  // Master-only route check (livello "master", additivo al ruolo)
  if (masterOnly && !isMaster()) {
    return <Navigate to="/dashboard" replace />;
  }

  // Admin-only route check
  if (adminOnly && !isAdmin()) {
    // Find the first accessible route for this user
    const defaultRoute = user.modules_access?.includes('general') ? '/clients' : '/login';
    return <Navigate to={defaultRoute} replace />;
  }

  // Module access check
  if (requiredModule && !hasModuleAccess(requiredModule)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/attiva-account" element={<AttivaAccount />} />
      {/* Area clienti (clienti finali): accesso separato con codice SMS */}
      <Route path="/area-clienti" element={<AreaClienti />} />
      <Route path="/consensi" element={<Consensi />} />
      
      {/* Admin Dashboard */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      
      {/* General CRM */}
      <Route path="/clients" element={<ProtectedRoute requiredModule="general"><Clients /></ProtectedRoute>} />
      <Route path="/caf/appuntamenti" element={<ProtectedRoute requiredModule="caf"><CalendarioAppuntamenti /></ProtectedRoute>} />
      <Route path="/caf/isee" element={<ProtectedRoute requiredModule="caf"><CafPratiche tipo="isee" /></ProtectedRoute>} />
      <Route path="/caf/730" element={<ProtectedRoute requiredModule="caf"><CafPratiche tipo="730" /></ProtectedRoute>} />
      <Route path="/patronato/naspi" element={<ProtectedRoute requiredModule="patronato"><CafPratiche tipo="naspi" /></ProtectedRoute>} />
      <Route path="/patronato/assegno-unico" element={<ProtectedRoute requiredModule="patronato"><CafPratiche tipo="assegno_unico" /></ProtectedRoute>} />
      <Route path="/patronato/maternita-inps" element={<ProtectedRoute requiredModule="patronato"><CafPratiche tipo="maternita_inps" /></ProtectedRoute>} />
      <Route path="/patronato/bonus-nido" element={<ProtectedRoute requiredModule="patronato"><CafPratiche tipo="bonus_nido" /></ProtectedRoute>} />
      <Route path="/patronato/test-a2" element={<ProtectedRoute requiredModule="patronato"><CafPratiche tipo="test_a2" /></ProtectedRoute>} />
      <Route path="/patronato/ricongiungimento" element={<ProtectedRoute requiredModule="patronato"><CafPratiche tipo="ricongiungimento" /></ProtectedRoute>} />
      <Route path="/patronato/inps" element={<ProtectedRoute requiredModule="patronato"><PatronatoInps /></ProtectedRoute>} />
      <Route path="/patronato/assegno-sociale" element={<ProtectedRoute requiredModule="patronato"><CafPratiche tipo="assegno_sociale" /></ProtectedRoute>} />
      <Route path="/patronato/assegno-invalidita" element={<ProtectedRoute requiredModule="patronato"><CafPratiche tipo="assegno_invalidita" /></ProtectedRoute>} />
      <Route path="/patronato/assegno-invalidita-civile" element={<ProtectedRoute requiredModule="patronato"><CafPratiche tipo="assegno_invalidita_civile" /></ProtectedRoute>} />
      <Route path="/patronato/pensione-invalidita-civile" element={<ProtectedRoute requiredModule="patronato"><CafPratiche tipo="pensione_invalidita_civile" /></ProtectedRoute>} />
      <Route path="/patronato/indennita-frequenza" element={<ProtectedRoute requiredModule="patronato"><CafPratiche tipo="indennita_frequenza" /></ProtectedRoute>} />
      <Route path="/patronato/bonus-nuovi-nati" element={<ProtectedRoute requiredModule="patronato"><CafPratiche tipo="bonus_nuovi_nati" /></ProtectedRoute>} />
      <Route path="/patronato/bonus-mamme" element={<ProtectedRoute requiredModule="patronato"><CafPratiche tipo="bonus_mamme" /></ProtectedRoute>} />
      <Route path="/patronato/assegno-inclusione" element={<ProtectedRoute requiredModule="patronato"><CafPratiche tipo="assegno_inclusione" /></ProtectedRoute>} />
      <Route path="/servizi/cambio-residenza" element={<ProtectedRoute requiredModule="services"><CafPratiche tipo="cambio_residenza" /></ProtectedRoute>} />
      <Route path="/catalogo-clienti" element={<ProtectedRoute requiredModule="general"><CatalogoClienti /></ProtectedRoute>} />
      <Route path="/news-clienti" element={<ProtectedRoute requiredModule="general"><NewsClienti /></ProtectedRoute>} />
      <Route path="/messaggi-clienti" element={<ProtectedRoute requiredModule="general"><MessaggiClienti /></ProtectedRoute>} />
      <Route path="/annunci-app" element={<ProtectedRoute requiredModule="general"><AnnunciApp /></ProtectedRoute>} />
      <Route path="/registrazioni-app" element={<ProtectedRoute requiredModule="general"><RegistrazioniApp /></ProtectedRoute>} />
      <Route path="/delega" element={<ProtectedRoute requiredModule="general"><Delega /></ProtectedRoute>} />
      <Route path="/scanner-documenti" element={<ProtectedRoute requiredModule="general"><ScannerDocumenti /></ProtectedRoute>} />
      <Route path="/nuova-da-bolletta" element={<ProtectedRoute requiredModule="general"><NuovaDaBolletta /></ProtectedRoute>} />
      <Route path="/company-profile" element={<ProtectedRoute adminOnly><CompanyProfile /></ProtectedRoute>} />
      
      {/* Consulting (gruppo Servizi) */}
      <Route path="/consulting" element={<ProtectedRoute requiredModule="services"><Consulting /></ProtectedRoute>} />
      <Route path="/dimissioni" element={<ProtectedRoute requiredModule="patronato"><Dimissioni /></ProtectedRoute>} />
      <Route path="/permessi-soggiorno" element={<ProtectedRoute requiredModule="patronato"><PermessiSoggiorno /></ProtectedRoute>} />
      <Route path="/permessi-soggiorno/:id" element={<ProtectedRoute requiredModule="services"><PermessiSoggiorno /></ProtectedRoute>} />
      <Route path="/imu" element={<ProtectedRoute requiredModule="caf"><Imu /></ProtectedRoute>} />
      <Route path="/estratto-inps" element={<ProtectedRoute requiredModule="patronato"><EstrattoInps /></ProtectedRoute>} />
      <Route path="/assegno-maternita" element={<ProtectedRoute requiredModule="services"><AssegnoMaternita /></ProtectedRoute>} />
      
      {/* Services */}
      <Route path="/canone-rai" element={<ProtectedRoute requiredModule="services"><CanoneRai /></ProtectedRoute>} />
      <Route path="/ospitalita" element={<ProtectedRoute><Ospitalita /></ProtectedRoute>} />
      <Route path="/registro-attivita" element={<ProtectedRoute superadminOnly><AuditLog /></ProtectedRoute>} />
      <Route path="/mutui" element={<ProtectedRoute requiredModule="services"><Mutui /></ProtectedRoute>} />
      <Route path="/utilities" element={<ProtectedRoute requiredModule="utenze"><Utilities /></ProtectedRoute>} />
      <Route path="/sub-agenti" element={<ProtectedRoute requiredModule="utenze"><SubAgenti /></ProtectedRoute>} />
      <Route path="/luce-gas/fatture" element={<ProtectedRoute requiredModule="luce_gas"><FattureEstrazione /></ProtectedRoute>} />
      <Route path="/luce-gas/simulazione" element={<ProtectedRoute requiredModule="luce_gas"><SimulazioneOfferta /></ProtectedRoute>} />
      <Route path="/luce-gas/pratiche" element={<ProtectedRoute requiredModule="luce_gas"><PraticheLuceGas /></ProtectedRoute>} />
      <Route path="/luce-gas/scadenze" element={<ProtectedRoute requiredModule="luce_gas"><ScadenzeLuceGas /></ProtectedRoute>} />
      
      {/* Insurance CRM */}
      <Route path="/quotes" element={<ProtectedRoute requiredModule="insurance"><Quotes /></ProtectedRoute>} />
      <Route path="/policies" element={<ProtectedRoute requiredModule="insurance"><Policies /></ProtectedRoute>} />
      <Route path="/claims" element={<ProtectedRoute requiredModule="insurance"><Claims /></ProtectedRoute>} />
      <Route path="/renewals" element={<ProtectedRoute requiredModule="insurance"><Renewals /></ProtectedRoute>} />
      <Route path="/payments" element={<ProtectedRoute requiredModule="insurance"><Payments /></ProtectedRoute>} />
      
      {/* Telecom CRM */}
      <Route path="/telecom/contracts" element={<ProtectedRoute requiredModule="telecom"><TelecomContracts /></ProtectedRoute>} />
      <Route path="/telecom/sims" element={<ProtectedRoute requiredModule="telecom"><TelecomSIMs /></ProtectedRoute>} />
      <Route path="/telecom/billing" element={<ProtectedRoute requiredModule="telecom"><TelecomBilling /></ProtectedRoute>} />
      <Route path="/telecom/scadenze" element={<ProtectedRoute requiredModule="telecom"><TelecomScadenze /></ProtectedRoute>} />

      {/* Agenti - solo livello master */}
      <Route path="/agenti" element={<ProtectedRoute masterOnly><Agenti /></ProtectedRoute>} />
      
      {/* Admin Only */}
      <Route path="/users" element={<ProtectedRoute adminOnly><UserManagement /></ProtectedRoute>} />
      <Route path="/backup" element={<ProtectedRoute superadminOnly><BackupRestore /></ProtectedRoute>} />
      <Route path="/audit-log" element={<ProtectedRoute superadminOnly><AuditLog /></ProtectedRoute>} />
      
      {/* Common */}
      <Route path="/search" element={<ProtectedRoute><SearchResults /></ProtectedRoute>} />
      <Route path="/ai-assistant" element={<ProtectedRoute><AIAssistant /></ProtectedRoute>} />
      
      {/* Redirects */}
      <Route path="/" element={<Navigate to="/clients" replace />} />
      <Route path="*" element={<ProtectedRoute><NotFound /></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <ErrorBoundary>
              <AppRoutes />
            </ErrorBoundary>
            <Toaster position="top-center" richColors />
            <TrascinaFile />
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
